import {
  Alert,
  Box,
  Button,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import {
  alpha,
  styled
} from "@mui/material/styles";

// Profile MFA
export const Separator = styled(Box)(({ theme }) => ({
  backgroundColor: theme.customColors.border,
  height: "22px",
  marginInline: "12px",
  width: "1px"
}));
export const ManageAuthenticationButton = styled("button")(({ theme }) => ({
  ...theme.typography.body1,
  fontSize: "12px",
  ":hover": {
    color: theme.customColors.textPrimary,
    transition: "all 0.2s ease"
  },
  background: "none",
  border: 0,
  color: theme.customColors.systemSecondary01,
  cursor: "pointer",
  fontWeight: 600,
  lineHeight: "100%",
  padding: 0,
  textDecoration: "none",
  whiteSpace: "nowrap"
}));

export const ProfileMfaModal = styled(Modal)(() => ({
  zIndex: 1400
}));

export const ProfileMfaModalContent = styled(Box)(({ theme }) => ({
  "&:focus-visible": {
    outline: "none"
  },
  left: "50%",
  maxHeight: "90vh",
  maxWidth: "840px",
  overflow: "auto",
  position: "absolute",
  top: "50%",
  transform: "translate(-50%, -50%)",
  width: "calc(100% - 32px)",

  [theme.breakpoints.up("sm")]: {
    width: "calc(100% - 48px)"
  }
}));

// SelectFavourite
export const FavouritePanel = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.customColors.backgroundPrimary,
  border: `1px solid ${theme.customColors.border}`,
  borderRadius: "16px",
  boxShadow: `0 0 0 1px ${alpha(theme.customColors.borderPrimaryCta, 0.24)}`,
  color: theme.customColors.textPrimary,
  gap: theme.spacing(3),
  maxWidth: "840px",
  padding: "24px",
  width: "100%",

  [theme.breakpoints.up("sm")]: {
    padding: "32px"
  }
}));

export const FavouriteTitle = styled(Typography)(() => ({
  fontSize: "1.5rem",
  fontWeight: 700,
  lineHeight: 1.2
}));

export const FavouriteDescription = styled(Typography)(({ theme }) => ({
  color: theme.customColors.textPrimary,
  fontSize: "1.125rem",
  lineHeight: 1.35
}));

export const FavouriteCloseButton = styled(IconButton)(({ theme }) => ({
  color: theme.customColors.textPrimary,
  marginRight: "-8px",
  marginTop: "-8px"
}));

export const MfaMethodOption = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "$isSelected"
})<{ $isSelected: boolean }>(({ $isSelected, theme }) => ({
  border: `1px solid ${$isSelected ? theme.customColors.borderPrimaryCta : theme.customColors.border}`,
  borderRadius: "8px",
  transition: "border-color 160ms ease",
  width: "100%"
}));

export const MfaMethodButton = styled("button")(() => ({
  "&:disabled": { cursor: "default" },
  backgroundColor: "transparent",
  border: 0,
  borderRadius: "8px",
  color: "inherit",
  cursor: "pointer",
  font: "inherit",
  minHeight: "62px",
  padding: "10px 12px",
  textAlign: "left",
  width: "100%"
}));

export const MfaMethodLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "$isSelected"
})<{ $isSelected: boolean }>(({ $isSelected, theme }) => ({
  color: $isSelected ? theme.customColors.backgroundPrimaryCta : theme.customColors.textPrimary,
  fontWeight: 700,
  lineHeight: 1.1
}));

export const MfaMethodDetail = styled(Typography)(({ theme }) => ({
  color: theme.customColors.textPrimary,
  fontSize: "0.875rem",
  lineHeight: 1.2
}));

export const MfaMethodAction = styled("button")(({ theme }) => ({
  "&:disabled": { color: theme.customColors.textDisabled, cursor: "default" },
  "&:hover": {
    color: theme.customColors.backgroundPrimaryCtaHover
  },
  alignSelf: "flex-start",
  backgroundColor: "transparent",
  border: "none",
  color: theme.customColors.backgroundPrimaryCta,
  cursor: "pointer",
  fontSize: "0.875rem",
  fontWeight: 700,
  lineHeight: 1.2,
  margin: "0 12px 10px",
  padding: "3.5px 0",
  textAlign: "left",
  width: "auto"
}));

export const MfaMethodSelectedIcon = styled(Typography)(({ theme }) => ({
  color: theme.customColors.backgroundPrimaryCta,
  fontSize: "1.5rem",
  lineHeight: 1
}));

export const FavouriteSaveButton = styled(Button)(({ theme }) => ({
  "&:disabled": {
    backgroundColor: theme.customColors.backgroundDisabledAlternative,
    color: theme.customColors.textDisabled
  },
  "&:hover": {
    backgroundColor: theme.customColors.backgroundPrimaryCtaHover
  },
  alignSelf: "flex-start",
  backgroundColor: theme.customColors.backgroundPrimaryCta,
  borderRadius: "8px",
  color: theme.customColors.textPrimaryCta,
  fontSize: "0.875rem",
  fontWeight: 700,
  minHeight: "43px",
  minWidth: "73px",
  textTransform: "none"
}));

// Modal
export const SetupLoadingContainer = styled(Stack)(({ theme }) => ({
  alignItems: "center",
  backgroundColor: theme.customColors.backgroundPrimary,
  border: `1px solid ${theme.customColors.border}`,
  borderRadius: "16px",
  justifyContent: "center",
  minHeight: "360px",
  width: "100%"
}));

export const SetupPanel = styled(Stack)(({ theme }) => ({
  alignItems: "center",
  backgroundColor: theme.customColors.backgroundPrimary,
  border: `1px solid ${theme.customColors.border}`,
  borderRadius: "16px",
  color: theme.customColors.textPrimary,
  gap: theme.spacing(3),
  maxWidth: "840px",
  padding: "24px",
  width: "100%",

  [theme.breakpoints.up("sm")]: {
    padding: "34px 42px 32px"
  }
}));

export const SetupHeader = styled(Stack)(({ theme }) => ({
  alignItems: "center",
  gap: theme.spacing(2),
  width: "100%"
}));

export const SetupTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.5rem",
  fontWeight: 700,
  lineHeight: 1.2,
  textAlign: "center",

  [theme.breakpoints.up("sm")]: {
    fontSize: "1.875rem"
  }
}));

export const SetupDescription = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  lineHeight: 1.35,
  textAlign: "center",

  [theme.breakpoints.up("sm")]: {
    fontSize: "1.125rem"
  }
}));

export const SetupQrImage = styled("img")(({ theme }) => ({
  borderRadius: 10,
  height: 168,
  width: 168,

  [theme.breakpoints.up("sm")]: {
    height: 190,
    width: 190
  }
}));

export const SetupWarning = styled(Alert)(({ theme }) => ({
  "& .MuiAlert-icon": {
    color: theme.customColors.textWarning
  },
  alignItems: "center",
  backgroundColor: theme.customColors.backgroundWarning,
  border: `1px solid ${theme.customColors.borderWarning}`,
  borderRadius: "4px",
  color: theme.customColors.textTag,
  fontSize: "1rem",
  fontWeight: 700,
  width: "100%"
}));

export const SetupFieldsContainer = styled(Stack)(({ theme }) => ({
  alignItems: "center",
  gap: theme.spacing(2),
  width: "100%"
}));

export const SetupHint = styled(Typography)(() => ({
  fontSize: "0.875rem",
  lineHeight: 1.35,
  textAlign: "center"
}));

export const SetupFieldContainer = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(0.75),
  width: "100%"
}));

export const SetupFieldLabel = styled(Typography)(() => ({
  fontSize: "0.875rem",
  fontWeight: 700,
  lineHeight: 1.2
}));

export const SetupTextField = styled(TextField)(({ theme }) => ({
  "& .MuiInputBase-input": {
    color: theme.customColors.textPrimary
  },
  "& .MuiInputBase-input.Mui-disabled": {
    color: theme.customColors.textPrimary,
    WebkitTextFillColor: theme.customColors.textPrimary
  },
  "& .MuiOutlinedInput-input": {
    padding: "13px 12px"
  },
  "& .MuiOutlinedInput-root": {
    "&.Mui-focused fieldset": {
      borderColor: theme.customColors.borderPrimaryCta
    },
    backgroundColor: "transparent",
    borderRadius: "8px"
  },
  "& .MuiOutlinedInput-root fieldset": {
    borderColor: theme.customColors.border
  },
  "& fieldset": {
    top: 0
  },
  "& legend": {
    display: "none"
  }
}));

export const SetupCodeTextField = styled(SetupTextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root:hover fieldset": {
    borderColor: theme.customColors.borderAccent
  }
}));

export const SetupErrorText = styled(Typography)(({ theme }) => ({
  color: theme.customColors.textError,
  fontSize: "0.875rem",
  textAlign: "center"
}));

export const SetupActions = styled(Stack)(({ theme }) => ({
  flexDirection: "column",
  gap: theme.spacing(2),
  width: "100%",

  [theme.breakpoints.up("sm")]: {
    flexDirection: "row"
  }
}));

export const SetupCancelButton = styled(Button)(({ theme }) => ({
  "&:hover": {
    backgroundColor: "transparent",
    border: `2px solid ${theme.customColors.textPrimary}`
  },
  border: `2px solid ${theme.customColors.textPrimary}`,
  borderRadius: "7px",
  color: theme.customColors.textPrimary,
  fontSize: "0.875rem",
  fontWeight: 700,
  height: "42px",
  textTransform: "none"
}));

export const SetupConfirmButton = styled(Button)(({ theme }) => ({
  "&:disabled": {
    backgroundColor: theme.customColors.backgroundDisabledAlternative,
    color: theme.customColors.textDisabled
  },
  "&:hover": {
    backgroundColor: theme.customColors.backgroundPrimaryCtaHover
  },
  backgroundColor: theme.customColors.backgroundPrimaryCta,
  borderRadius: "7px",
  color: theme.customColors.textPrimaryCta,
  fontSize: "0.875rem",
  fontWeight: 700,
  height: "42px",
  textTransform: "none"
}));

// Success
export const SuccessPanel = styled(Stack)(({ theme }) => ({
  alignItems: "center",
  backgroundColor: theme.customColors.backgroundPrimary,
  border: `1px solid ${theme.customColors.border}`,
  borderRadius: "16px",
  color: theme.customColors.textPrimary,
  gap: theme.spacing(3),
  justifyContent: "center",
  minHeight: "346px",
  padding: "32px 24px",
  width: "100%",

  [theme.breakpoints.up("sm")]: {
    padding: "48px"
  }
}));

export const SuccessTextContainer = styled(Stack)(({ theme }) => ({
  alignItems: "center",
  gap: theme.spacing(2)
}));

export const SuccessTitle = styled(Typography)(() => ({
  fontSize: "1.5rem",
  fontWeight: 700,
  lineHeight: 1.2,
  textAlign: "center"
}));

export const SuccessDescription = styled(Typography)(() => ({
  fontSize: "1.125rem",
  lineHeight: 1.2,
  maxWidth: "460px",
  textAlign: "center"
}));

export const SuccessBackButton = styled(Button)(({ theme }) => ({
  "&:hover": {
    backgroundColor: "transparent",
    color: theme.customColors.backgroundPrimaryCtaHover
  },
  color: theme.customColors.backgroundPrimaryCta,
  fontSize: "0.875rem",
  fontWeight: 700,
  minWidth: "auto",
  padding: 0,
  textTransform: "none"
}));
